import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "flex items-center gap-x-2 text-sm text-gray-500" }
const _hoisted_2 = { class: "pt-8 border-t border-t-gray-200 space-y-6" }
const _hoisted_3 = { class: "flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg border border-gray-200 bg-white p-4 border-l-4 border-l-blue-500" }
const _hoisted_4 = { class: "flex-1 min-w-0" }
const _hoisted_5 = { class: "flex items-start gap-x-6" }
const _hoisted_6 = { class: "text-xs text-gray-500 mb-1" }
const _hoisted_7 = {
  key: 0,
  class: "flex-shrink-0 rounded-lg border border-gray-200 bg-white px-4 py-3 min-w-[7rem]"
}
const _hoisted_8 = { class: "text-xs text-gray-500" }
const _hoisted_9 = {
  key: 0,
  class: "text-xs text-gray-400 mb-1 max-w-[12rem] truncate"
}
const _hoisted_10 = { class: "text-3xl font-bold text-gray-900 leading-none mb-2" }
const _hoisted_11 = { class: "flex items-center gap-x-1.5" }
const _hoisted_12 = { class: "flex items-center gap-x-1" }
const _hoisted_13 = ["disabled"]
const _hoisted_14 = ["disabled"]
const _hoisted_15 = { class: "flex border-b border-gray-200" }
const _hoisted_16 = { class: "rounded-lg border border-gray-200 bg-white p-4" }
const _hoisted_17 = { class: "flex items-center gap-x-2 mb-4" }
const _hoisted_18 = { class: "text-sm font-semibold text-gray-700" }
const _hoisted_19 = {
  key: 1,
  class: "py-8 text-center text-sm text-gray-500"
}
const _hoisted_20 = { class: "rounded-lg border border-gray-200 bg-white p-6" }
const _hoisted_21 = { class: "flex items-center gap-x-2 mb-1" }
const _hoisted_22 = { class: "text-base font-semibold text-gray-900" }
const _hoisted_23 = { class: "text-sm text-gray-500 mb-6" }
const _hoisted_24 = {
  key: 0,
  class: "text-sm text-gray-500"
}
const _hoisted_25 = {
  key: 1,
  class: "text-sm text-gray-500"
}
const _hoisted_26 = { class: "space-y-3" }
const _hoisted_27 = { class: "flex items-center gap-x-4 w-full" }
const _hoisted_28 = ["onClick"]
const _hoisted_29 = {
  key: 1,
  class: "font-medium flex-1 min-w-0 truncate"
}
const _hoisted_30 = { class: "text-sm font-semibold text-gray-700 flex-shrink-0 min-w-[4rem] text-right" }
const _hoisted_31 = { class: "divide-y divide-gray-100" }
const _hoisted_32 = ["onClick"]
const _hoisted_33 = {
  key: 0,
  class: "text-xs text-gray-400"
}

import { ref, computed, watch, onBeforeUnmount } from 'vue';
import { CalendarDays, ChevronLeft, ChevronRight, Info, Clock } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import DashboardContentLayout from '@/components/layouts/dashboardLayout/content/DashboardContentLayout.vue';
import ExpansionPanel from '@/components/ui/ExpansionPanel.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Icon from '@/components/icons/Icon.vue';
import ActionModal from '@/components/common/actionModal/ActionModal.vue';
import DataQualityStatusBarTooltip from '@/components/common/dataQuality/DataQualityStatusBarTooltip.vue';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { QUALITY_CATEGORY_STYLES } from '@/components/common/dataQuality/qualityCategoryStyles';
import DataQualityHistoryChart from './DataQualityHistoryChart.vue';
import DataQualityViewSwitch from './DataQualityViewSwitch.vue';
import { DATA_QUALITY_ASSESSMENT_FORM_ID } from './constants';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { MutationType } from '@/plugins/store/mutations';
import { DataQualityAssessmentFarmSummary, DataQualityAssessmentMethod, IconSource, DataQualityAssessmentFarmLocationMethodAggregate, MultiStepFormState, Form } from '@/types';

interface PopulatedDataQualityAssessmentMethod extends DataQualityAssessmentFarmLocationMethodAggregate {
    _method: DataQualityAssessmentMethod | undefined;
}
interface PopulatedDataQualityAssessmentFarmSummary extends DataQualityAssessmentFarmSummary {
    _items: PopulatedDataQualityAssessmentMethod[];
}


export default /*@__PURE__*/_defineComponent({
  __name: 'HistoryView',
  setup(__props) {

function toLocalDateStr(d: Date): string {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function nearestMondayOnOrBefore(date: Date): string {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    return toLocalDateStr(d);
}

const toDate = new Date();
const fromDate = new Date(toDate);
fromDate.setMonth(fromDate.getMonth() - 6);
const toDateStr = toLocalDateStr(toDate);
const fromDateStr = toLocalDateStr(fromDate);

const DISABLE_NON_MONDAYS = [(date: Date) => date.getDay() !== 1];

// selectedDate may be a YYYY-MM-DD string or a full ISO string from the DatePicker
const selectedDate = ref<string>(nearestMondayOnOrBefore(toDate));

// Normalized to YYYY-MM-DD (local). DatePicker may emit a full ISO string.
const normalizedSelectedDate = computed(() => {
    if (!selectedDate.value) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(selectedDate.value)) return selectedDate.value;
    return toLocalDateStr(new Date(selectedDate.value));
});

const formattedDate = computed(() => {
    if (!normalizedSelectedDate.value) return '';
    const [year, month, day] = normalizedSelectedDate.value.split('-').map(Number);
    return i18n.global.d(new Date(year, month - 1, day), 'middle');
});

const canGoPrev = computed(() => {
    if (!normalizedSelectedDate.value) return false;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    return toLocalDateStr(new Date(y, m - 1, d - 7)) >= fromDateStr;
});

const canGoNext = computed(() => {
    if (!normalizedSelectedDate.value) return false;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    return toLocalDateStr(new Date(y, m - 1, d + 7)) <= toDateStr;
});

function prevWeek() {
    if (!canGoPrev.value) return;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    selectedDate.value = toLocalDateStr(new Date(y, m - 1, d - 7));
}

function nextWeek() {
    if (!canGoNext.value) return;
    const [y, m, d] = normalizedSelectedDate.value.split('-').map(Number);
    selectedDate.value = toLocalDateStr(new Date(y, m - 1, d + 7));
}

function goToday() {
    selectedDate.value = nearestMondayOnOrBefore(new Date());
}

const showFarms = ref(false);
const hiddenFarmIds = ref<number[]>([]);
const isLoadingHistory = ref(false);
const isLoadingDetail = ref(false);
const noDataForDate = ref(false);

const selectedDateDirectoryScore = computed(() => {
    const assessment = store.state.dataQualityAssessmentForDate;
    if (!assessment || isLoadingDetail.value || noDataForDate.value) return null;
    const id = assessment.quality_assessment_id;
    const key = store.state.dataQualityCategories?.find((c) => c.id === id)?.key ?? '';
    const styles = QUALITY_CATEGORY_STYLES[key];
    return {
        formatted: formatScore(assessment.score),
        categoryName: qualityLabel(id),
        dotClass: styles?.dot ?? 'bg-gray-400',
        textClass: styles?.text ?? 'text-gray-600',
    };
});

const history = computed(() => store.state.dataQualityHistory ?? []);

isLoadingHistory.value = true;
store.dispatch(ActionType.GetDataQualityHistory, { from: fromDateStr, to: toDateStr }).finally(() => {
    isLoadingHistory.value = false;
});

store.dispatch(ActionType.GetDataQualityCategories);
store.dispatch(ActionType.GetDataQualityAssessmentMethods);

let loadDetailSeq = 0;

async function loadDetailForDate(dateStr: string) {
    const seq = ++loadDetailSeq;
    isLoadingDetail.value = true;
    noDataForDate.value = false;
    store.commit(MutationType.SetDataQualityAssessmentForDate, null);
    try {
        const result = await store.dispatch(ActionType.GetDataQualityAssessmentForDate, { date: dateStr, options: { cacheResponse: false, ignoreCache: true } });
        if (seq !== loadDetailSeq) return;
        store.commit(MutationType.SetDataQualityAssessmentForDate, result || null);
        noDataForDate.value = !result;
    } catch {
        if (seq !== loadDetailSeq) return;
        noDataForDate.value = true;
    } finally {
        if (seq === loadDetailSeq) isLoadingDetail.value = false;
    }
}

onBeforeUnmount(() => {
    store.commit(MutationType.SetDataQualityAssessmentForDate, null);
});

loadDetailForDate(normalizedSelectedDate.value);

watch(normalizedSelectedDate, (date) => {
    if (date) loadDetailForDate(date);
});

function onChartDateSelected(date: string) {
    selectedDate.value = date;
}

function toggleFarm(farmId: number) {
    const idx = hiddenFarmIds.value.indexOf(farmId);
    if (idx === -1) {
        hiddenFarmIds.value = [...hiddenFarmIds.value, farmId];
    } else {
        hiddenFarmIds.value = hiddenFarmIds.value.filter((id) => id !== farmId);
    }
}

function qualityBadgeClass(qualityAssessmentId: number): string {
    const key = store.state.dataQualityCategories?.find((c) => c.id === qualityAssessmentId)?.key ?? '';
    return QUALITY_CATEGORY_STYLES[key]?.badge ?? 'bg-gray-100 text-gray-700';
}

function qualityLabel(qualityAssessmentId: number): string {
    return store.state.dataQualityCategories?.find((c) => c.id === qualityAssessmentId)?.name ?? '';
}

function formatScore(score: number | null | undefined): string {
    if (score == null) return '—';
    return score.toFixed(1);
}

const farmAssessmentSummaries = computed<PopulatedDataQualityAssessmentFarmSummary[]>(() => {
    const assessment = store.state.dataQualityAssessmentForDate;
    if (!assessment || !store.state.dataQualityCategories || !store.state.dataQualityAssessmentMethods) {
        return [];
    }
    return (
        assessment.farm_summaries?.map((f) => ({
            ...f,
            _items:
                f.method_aggregate
                    ?.map((m) => ({
                        ...m,
                        _method: store.state.dataQualityAssessmentMethods!.find((dm) => dm.id === m.assessment_method_id),
                    }))
                    .filter((m) => m._method) || [],
        })) || []
    );
});

const actionModalInitialState = ref<MultiStepFormState>({ form: null, confirmed: false, event: null });
const actionModalStepConfig = ref([{}, { deleteButton: false, editable: false }, { disableRefetch: true, submitActionType: ActionType.GetForm }, {}]);
const actionModalHeader = ref<{ icon_url: string; text: string } | null>(null);
const isLoadingActionForm = ref(false);

async function openForm(methodId: DataQualityAssessmentFarmLocationMethodAggregate['id'], open: CallableFunction) {
    try {
        if (isLoadingActionForm.value) return;
        isLoadingActionForm.value = true;
        const form: Form = await store.dispatch(ActionType.GetForm, {
            form: { form_id: DATA_QUALITY_ASSESSMENT_FORM_ID, resource_id: methodId },
        });
        actionModalInitialState.value = { ...actionModalInitialState.value, form };
        actionModalHeader.value = { icon_url: IconSource.DataQualityIcon as string, text: form.sections?.[0]?.name || '' };
        open();
    } catch {
        //
    } finally {
        isLoadingActionForm.value = false;
    }
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createBlock(DashboardContentLayout, {
    class: "space-y-8",
    heading: _ctx.$t('views.dataQuality.header.heading'),
    subheading: _ctx.$t('views.dataQuality.history.subheading')
  }, {
    "header-top-right": _withCtx(() => [
      _createVNode(DataQualityViewSwitch)
    ]),
    default: _withCtx(() => [
      _createElementVNode("div", _hoisted_1, [
        _createVNode(_unref(Info), {
          class: "h-4 w-4 shrink-0",
          "aria-hidden": "true"
        }),
        _createElementVNode("span", null, _toDisplayString(_ctx.$t('views.dataQuality.texts.trialNotice')), 1)
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", _hoisted_3, [
          _createVNode(_unref(CalendarDays), {
            class: "h-5 w-5 text-blue-500 flex-shrink-0",
            "aria-hidden": "true"
          }),
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createElementVNode("div", null, [
                _createElementVNode("p", _hoisted_6, _toDisplayString(_ctx.$t('views.dataQuality.history.viewingDataFor')), 1),
                _createVNode(DatePicker, {
                  value: selectedDate.value,
                  "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((selectedDate).value = $event)),
                  min: _unref(fromDateStr),
                  max: _unref(toDateStr),
                  "disable-dates": DISABLE_NON_MONDAYS,
                  "show-overlay": false,
                  position: "below",
                  class: "max-w-56"
                }, null, 8, ["value", "min", "max"])
              ]),
              (selectedDateDirectoryScore.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                    _createElementVNode("p", _hoisted_8, _toDisplayString(_ctx.$t('views.dataQuality.history.qualityScore')), 1),
                    (_unref(store).getters.activeDirectoryName)
                      ? (_openBlock(), _createElementBlock("p", _hoisted_9, _toDisplayString(_unref(store).getters.activeDirectoryName), 1))
                      : _createCommentVNode("", true),
                    _createElementVNode("p", _hoisted_10, _toDisplayString(selectedDateDirectoryScore.value.formatted), 1),
                    _createElementVNode("div", _hoisted_11, [
                      _createElementVNode("span", {
                        class: _normalizeClass(['h-2.5 w-2.5 rounded-full flex-shrink-0', selectedDateDirectoryScore.value.dotClass])
                      }, null, 2),
                      _createElementVNode("span", {
                        class: _normalizeClass(['text-xs font-semibold', selectedDateDirectoryScore.value.textClass])
                      }, _toDisplayString(selectedDateDirectoryScore.value.categoryName), 3)
                    ])
                  ]))
                : _createCommentVNode("", true)
            ])
          ]),
          _createElementVNode("div", _hoisted_12, [
            _createElementVNode("button", {
              disabled: !canGoPrev.value,
              class: "rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors",
              onClick: prevWeek
            }, [
              _createVNode(_unref(ChevronLeft), {
                class: "h-4 w-4",
                "aria-hidden": "true"
              })
            ], 8, _hoisted_13),
            _createElementVNode("button", {
              class: "text-sm font-medium text-blue-600 hover:underline px-1.5",
              onClick: goToday
            }, _toDisplayString(_ctx.$t('views.dataQuality.history.today')), 1),
            _createElementVNode("button", {
              disabled: !canGoNext.value,
              class: "rounded p-1.5 hover:bg-gray-100 disabled:opacity-40 transition-colors",
              onClick: nextWeek
            }, [
              _createVNode(_unref(ChevronRight), {
                class: "h-4 w-4",
                "aria-hidden": "true"
              })
            ], 8, _hoisted_14)
          ])
        ]),
        _createElementVNode("div", _hoisted_15, [
          _createElementVNode("button", {
            class: _normalizeClass([
                        'px-4 py-2.5 text-base font-medium transition-colors border-b-2 -mb-px',
                        !showFarms.value ? 'border-primary-800 text-primary-800' : 'border-transparent text-gray-500 hover:text-gray-700',
                    ]),
            onClick: _cache[1] || (_cache[1] = ($event: any) => (showFarms.value = false))
          }, _toDisplayString(_ctx.$t('views.dataQuality.history.tabs.directory')), 3),
          _createElementVNode("button", {
            class: _normalizeClass(['px-4 py-2.5 text-base font-medium transition-colors border-b-2 -mb-px', showFarms.value ? 'border-primary-800 text-primary-800' : 'border-transparent text-gray-500 hover:text-gray-700']),
            onClick: _cache[2] || (_cache[2] = ($event: any) => (showFarms.value = true))
          }, _toDisplayString(_ctx.$t('views.dataQuality.history.tabs.farms')), 3)
        ]),
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("div", _hoisted_17, [
            _createElementVNode("span", _hoisted_18, _toDisplayString(_ctx.$t('views.dataQuality.history.chart.title')), 1),
            _createVNode(_unref(TooltipProvider), { "delay-duration": 200 }, {
              default: _withCtx(() => [
                _createVNode(_unref(Tooltip), null, {
                  default: _withCtx(() => [
                    _createVNode(_unref(TooltipTrigger), {
                      "aria-label": _ctx.$t('views.dataQuality.history.chart.categoryNote')
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(Info), {
                          class: "h-4 w-4 text-gray-400 flex-shrink-0",
                          "aria-hidden": "true"
                        })
                      ]),
                      _: 1
                    }, 8, ["aria-label"]),
                    _createVNode(_unref(TooltipContent), {
                      "collision-padding": 12,
                      class: "max-w-xs"
                    }, {
                      default: _withCtx(() => [
                        _createElementVNode("p", null, _toDisplayString(_ctx.$t('views.dataQuality.history.chart.categoryNote')), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            })
          ]),
          (history.value.length > 0)
            ? (_openBlock(), _createBlock(DataQualityHistoryChart, {
                key: 0,
                history: history.value,
                "show-farms": showFarms.value,
                "hidden-farm-ids": hiddenFarmIds.value,
                "selected-date-str": normalizedSelectedDate.value,
                categories: _unref(store).state.dataQualityCategories ?? [],
                "directory-name": _unref(store).getters.activeDirectoryName,
                onDateSelected: onChartDateSelected
              }, null, 8, ["history", "show-farms", "hidden-farm-ids", "selected-date-str", "categories", "directory-name"]))
            : (!isLoadingHistory.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_19, _toDisplayString(_ctx.$t('views.dataQuality.history.noHistory')), 1))
              : _createCommentVNode("", true)
        ]),
        _createElementVNode("div", _hoisted_20, [
          _createElementVNode("div", _hoisted_21, [
            _createVNode(_unref(Clock), {
              class: "h-5 w-5 text-gray-500 flex-shrink-0",
              "aria-hidden": "true"
            }),
            _createElementVNode("h2", _hoisted_22, _toDisplayString(_ctx.$t('views.dataQuality.history.snapshot.title', { date: formattedDate.value })), 1)
          ]),
          _createElementVNode("p", _hoisted_23, _toDisplayString(_ctx.$t('views.dataQuality.history.snapshot.subtitle')), 1),
          (isLoadingDetail.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_24, _toDisplayString(_ctx.$t('views.dataQuality.history.loading')), 1))
            : (noDataForDate.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_25, _toDisplayString(_ctx.$t('views.dataQuality.history.noDataForDate', { date: formattedDate.value })), 1))
              : (farmAssessmentSummaries.value.length > 0)
                ? (_openBlock(), _createBlock(ActionModal, {
                    key: 2,
                    "initial-step-index": 1,
                    "initial-state": actionModalInitialState.value,
                    "step-config": actionModalStepConfig.value,
                    header: actionModalHeader.value
                  }, {
                    default: _withCtx(({ open }) => [
                      _createElementVNode("div", _hoisted_26, [
                        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(farmAssessmentSummaries.value, (summary) => {
                          return (_openBlock(), _createBlock(ExpansionPanel, {
                            key: summary.id
                          }, {
                            header: _withCtx(() => [
                              _createElementVNode("div", _hoisted_27, [
                                _createVNode(DataQualityStatusBarTooltip, {
                                  "quality-assessment-id": summary.quality_assessment_id
                                }, null, 8, ["quality-assessment-id"]),
                                _createVNode(Icon, {
                                  src: _unref(IconSource).FarmsPrimary,
                                  class: "h-7 w-7 flex-shrink-0",
                                  "aria-hidden": "true"
                                }, null, 8, ["src"]),
                                (showFarms.value)
                                  ? (_openBlock(), _createElementBlock("button", {
                                      key: 0,
                                      class: _normalizeClass(['text-left font-medium flex-1 min-w-0 truncate transition-opacity', hiddenFarmIds.value.includes(summary.farm.id) ? 'opacity-40' : '']),
                                      onClick: _withModifiers(($event: any) => (toggleFarm(summary.farm.id)), ["stop"])
                                    }, _toDisplayString(summary.farm.name), 11, _hoisted_28))
                                  : (_openBlock(), _createElementBlock("span", _hoisted_29, _toDisplayString(summary.farm.name), 1)),
                                _createElementVNode("span", {
                                  class: _normalizeClass(['inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-semibold flex-shrink-0', qualityBadgeClass(summary.quality_assessment_id)])
                                }, _toDisplayString(qualityLabel(summary.quality_assessment_id)), 3),
                                _createElementVNode("span", _hoisted_30, _toDisplayString(_ctx.$t('views.dataQuality.history.snapshot.score', { score: formatScore(summary.score) })), 1)
                              ])
                            ]),
                            default: _withCtx(() => [
                              _createElementVNode("ul", _hoisted_31, [
                                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(summary._items, (item) => {
                                  return (_openBlock(), _createElementBlock("li", {
                                    key: `method-${summary.id}-${item.id}`,
                                    class: "relative hover:bg-gray-100 transition-colors flex items-center"
                                  }, [
                                    _createVNode(DataQualityStatusBarTooltip, {
                                      "quality-assessment-id": item.quality_assessment_id,
                                      class: "absolute left-6"
                                    }, null, 8, ["quality-assessment-id"]),
                                    _createElementVNode("button", {
                                      class: "flex pl-32 outline-none flex-grow items-center justify-between space-x-2 pr-6 py-6",
                                      onClick: ($event: any) => (openForm(item.id, open))
                                    }, [
                                      _createElementVNode("div", null, _toDisplayString(item._method?.name), 1),
                                      _createVNode(Icon, {
                                        src: _unref(IconSource).Right,
                                        class: "h-3.5 w-3.5 flex-shrink-0",
                                        "aria-hidden": "true"
                                      }, null, 8, ["src"])
                                    ], 8, _hoisted_32)
                                  ]))
                                }), 128))
                              ])
                            ]),
                            _: 2
                          }, 1024))
                        }), 128))
                      ])
                    ]),
                    _: 1
                  }, 8, ["initial-state", "step-config", "header"]))
                : _createCommentVNode("", true)
        ]),
        (normalizedSelectedDate.value)
          ? (_openBlock(), _createElementBlock("p", _hoisted_33, _toDisplayString(_ctx.$t('views.dataQuality.history.footer', { date: formattedDate.value })), 1))
          : _createCommentVNode("", true)
      ])
    ]),
    _: 1
  }, 8, ["heading", "subheading"]))
}
}

})